// File: src/lib/graphql/queries/auth.ts
// Purpose: Authentication & User GraphQL Queries and Mutations
// VIBE - Auth API

import { gql } from 'graphql-request'

// ============================================
// TYPES
// ============================================

export interface User {
  id: string
  _id?: string  // Support both id and _id
  email: string
  username?: string
  name?: string
  role?: string
  createdAt?: string
  updatedAt?: string
}
export interface UpdateEmployeeResumeInput {
  employeeId: string | null;
  resume: File | null|string; // assuming the resume is a File object
}

// Output type of the mutation
export interface UpdateEmployeeResumeResponse {
  resume: string; // could be URL or filename returned from server
}

export interface Employee {
  _id: string
  id?: string
  employeeId?: string
  firstName: string
  lastName: string
  email: string
  phone?: string
  mobile_number?: string
  countryCode?: string
  languages?: string[]
  joining_date?: string
  skills?: string[]
  designation?: string | {
    id?: string
    name?: string
    code?: string
  }
  department?: string | {
    id?: string
    name?: string
    code?: string
  }
  organization?: {
    id: string
    name: string
    code: string
  }
  shift?: string
  azureId?: string
  azureTenantId?: string
  azureEmail?: string
  azureDisplayName?: string
  ssoEnabled?: boolean
  lastAzureLogin?: string
  azureGroups?: string[]
  is_active?: boolean
  is_deleted?: boolean
  isActive?: boolean
  createdAt: string
  updatedAt: string
  role?: {
    _id: string
    title: string
    is_active: boolean
  }
  documents?: Array<{
    url: string
    type: string
    valid: boolean
  }>
  leave_requests?: Array<{
    leveId: string
    status: string
    description: string
  }>
  resume?: string
}

export interface AuthPayload {
  token: string
  user: User
}

export interface RegisterInput {
  username: string | null
  password: string | null
  email: string | null
}

export interface LoginInput {
  email: string | null
  password: string | null
}



// Helper types for paginated responses
interface Edge<T> {
  node: T
  cursor: string
}

interface PageInfo {
  hasNextPage: boolean
  endCursor?: string
}

interface PaginatedUsersResponse {
  edges: Edge<User>[]
  pageInfo: PageInfo
  totalCount: number
}

interface CreateUserInput {
  email: string
  password: string
  name: string
  role?: string
}

// ============================================
// QUERIES
// ============================================

export const ME_QUERY = gql`
  query Me {
    me {
      id
      email
      name
      role
      createdAt
      updatedAt
    }
  }
`

export const ME_EMPLOYEE_QUERY = gql`
  query MeEmployee {
    meEmployee {
      azureId
      azureTenantId
      azureEmail
      azureDisplayName
      ssoEnabled
      lastAzureLogin
      azureGroups
      _id
      firstName
      lastName
      email
      countryCode
      mobile_number
      languages
      joining_date
      skills
      designation
      department
      shift
      is_active
      is_deleted
      createdAt
      updatedAt
      resume
      role {
        _id
        title
        is_active
      }
      documents {
        url
        type
        valid
      }
      leave_requests {
        leveId
        status
        description
      }
    }
  }
`

export const USER_QUERY = gql`
  query User($id: ID!) {
    user(id: $id) {
      id
      email
      name
      role
      createdAt
      updatedAt
    }
  }
`

export const USERS_QUERY = gql`
  query Users($first: Int, $after: String, $filter: String) {
    users(first: $first, after: $after, filter: $filter) {
      edges {
        node {
          id
          email
          name
          role
          createdAt
          updatedAt
        }
        cursor
      }
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
    }
  }
`

// ============================================
// MUTATIONS
// ============================================

export const REGISTER_MUTATION = gql`
  mutation Register($input: RegisterInput) {
    register(input: $input) {
      token
      user {
        _id
        username
        email
      }
    }
  }
`

export const LOGIN_MUTATION = gql`
  mutation Login($input: LoginInput!) {
    login(input: $input) {
      token
      user {
        _id
        username
        email
      }
    }
  }
`

export const CREATE_USER_MUTATION = gql`
  mutation CreateUser($input: CreateUserInput!) {
    createUser(input: $input) {
      id
      email
      name
      role
      createdAt
    }
  }
`

export const UPDATE_EMPLOYEE_RESUME_MUTATION = gql`
  mutation UpdateEmployeeResume($input: UpdateEmployeeResumeInput!) {
    updateEmployeeResume(input: $input) {
      resume
    }
  }
`;

// ============================================
// API SERVICE FUNCTIONS
// ============================================

import { executeGraphQL, setAuthToken } from '../client'

export class AuthAPI {
  // Queries
  static async getCurrentUser(): Promise<User> {
    const { me } = await executeGraphQL<{ me: User }>(ME_QUERY)
    return me
  }

  static async getCurrentEmployee(): Promise<Employee> {
    const { meEmployee } = await executeGraphQL<{ meEmployee: Employee }>(ME_EMPLOYEE_QUERY)
    return meEmployee
  }

  static async getUser(id: string): Promise<User> {
    const { user } = await executeGraphQL<{ user: User }>(USER_QUERY, { id })
    return user
  }

  static async getUsers(first: number = 10, after?: string, filter?: string): Promise<PaginatedUsersResponse> {
    const { users } = await executeGraphQL<{ users: PaginatedUsersResponse }>(USERS_QUERY, {
      first,
      after,
      filter
    })
    return users
  }

  // Mutations
  static async register(input?: RegisterInput): Promise<AuthPayload> {
    // Default to null values if no input provided
    const defaultInput: RegisterInput = {
      username: null,
      password: null,
      email: null
    }

    const { register } = await executeGraphQL<{ register: AuthPayload }>(
      REGISTER_MUTATION,
      { input: input || defaultInput }
    )

    // Store token and set in client (only in browser)
    if (register.token && typeof window !== 'undefined') {
      localStorage.setItem('authToken', register.token)
      setAuthToken(register.token)
    }

    return register
  }

  static async login(input: LoginInput): Promise<AuthPayload> {
    try {
      const { login } = await executeGraphQL<{ login: AuthPayload }>(
        LOGIN_MUTATION,
        { input }
      )

      // Check if login response exists first
      if (!login) {
        throw new Error('Invalid login response')
      }

      // Store token and set in client (only in browser)
      if (login.token && typeof window !== 'undefined') {
        localStorage.setItem('authToken', login.token)
        setAuthToken(login.token)
      }

      // Normalize user object to always have 'id'
      if (login.user && login.user._id && !login.user.id) {
        login.user.id = login.user._id
      }

      return login
    } catch (error) {
      console.error('Login failed:', error)
      throw error
    }
  }

  static async loginWithSSO(params: { provider: string; redirectUrl: string }): Promise<{ authUrl?: string; token?: string }> {
    // Mock SSO implementation - in production, this would call the actual SSO endpoint
    // For now, return a mock response for development

    if (params.provider === 'azure') {
      // In production, this would redirect to Azure AD
      return {
        authUrl: `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=YOUR_CLIENT_ID&redirect_uri=${encodeURIComponent(params.redirectUrl)}&response_type=code&scope=openid%20profile%20email`
      }
    } else if (params.provider === 'google') {
      // In production, this would redirect to Google OAuth
      return {
        authUrl: `https://accounts.google.com/o/oauth2/v2/auth?client_id=YOUR_CLIENT_ID&redirect_uri=${encodeURIComponent(params.redirectUrl)}&response_type=code&scope=openid%20profile%20email`
      }
    } else if (params.provider === 'okta') {
      // In production, this would redirect to Okta
      return {
        authUrl: `https://YOUR_DOMAIN.okta.com/oauth2/default/v1/authorize?client_id=YOUR_CLIENT_ID&redirect_uri=${encodeURIComponent(params.redirectUrl)}&response_type=code&scope=openid%20profile%20email`
      }
    }

    // For development, return a mock token
    const mockToken = 'mock-jwt-token-' + Date.now()
    if (typeof window !== 'undefined') {
      localStorage.setItem('authToken', mockToken)
      setAuthToken(mockToken)
    }

    return { token: mockToken }
  }

  static async logout(): Promise<void> {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('authToken')
    }
    setAuthToken('')
  }

  static async createUser(input: CreateUserInput): Promise<User> {
    const { createUser } = await executeGraphQL<{ createUser: User }>(
      CREATE_USER_MUTATION,
      { input }
    )
    return createUser
  }

  static async uploadResume(input: UpdateEmployeeResumeInput): Promise<UpdateEmployeeResumeResponse> {
    const { data } = await executeGraphQL<{ data: UpdateEmployeeResumeResponse }>(
      UPDATE_EMPLOYEE_RESUME_MUTATION,
      { input }
    )

    return data;
  }


  // Utility functions
  static isAuthenticated(): boolean {
    if (typeof window === 'undefined') return false
    return !!localStorage.getItem('authToken')
  }

  static getStoredToken(): string | null {
    if (typeof window === 'undefined') return null
    return localStorage.getItem('authToken')
  }
}